import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyRegistrationGuard from './CompanyRegistrationGuard';
import AgentApprovalPendingModal from './AgentApprovalPendingModal';
import ExpiredRegistrationModal from './ExpiredRegistrationModal';
import ScoutPrereqModal from '../views/company/matchingsettings/ScoutPrereqModal';
import logo from '../assets/icons/tennavi_scout_logo2026_4.png';

/**
 * Props:
 * - onAllowed: function to call when all prereqs are satisfied
 * - children: render prop that receives { handleClick }
 */
const NonSearchSettingsGuard = ({ onAllowed, children, regGuardWidth = 460 }) => {
  const [isRegGuardOpen, setIsRegGuardOpen] = useState(false);
  const [regGuardMode, setRegGuardMode] = useState('incomplete'); // 'incomplete' | 'agent_pending'
  const [isAgentPendingOpen, setIsAgentPendingOpen] = useState(false);
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);
  const [isScoutPrereqOpen, setIsScoutPrereqOpen] = useState(false);

  const navigate = useNavigate();

  const handleClick = useCallback((fn) => {
    const companyRegistered = localStorage.getItem('company_is_fully_registered') === '1';
    const jobPublished     = localStorage.getItem('initial_job_post_published') === '1';
    const agentApproved    = localStorage.getItem('company_agent_approval_status') === '1';
    const registerExpired  = localStorage.getItem('register_expired') === '1';

    // --- CASE: registration expired ---
    if (registerExpired) {
      setIsExpiredModalOpen(true);
      return;
    }

    // --- CASE: initial job is NOT published ---
    if (!jobPublished) {
      // (1, 0, 0) → Company registered, job not published, agent NOT approved
      // → Agent Approval Pending modal (same as SearchSettingsGuard)
      if (companyRegistered && !agentApproved) {
        setIsAgentPendingOpen(true);
        return;
      }

      // (1, 0, 1) → Company registered, job not published, agent approved
      // → Step Two Registration Guard
      if (companyRegistered && agentApproved) {
        setIsScoutPrereqOpen(true);
        return;
      }

      // (0, 0, *) → Company not fully registered, job not published
      // → CompanyRegistrationGuard in 'incomplete' mode
      setRegGuardMode('incomplete');
      setIsRegGuardOpen(true);
      return;
    }

    // --- CASE: job IS published but company registration incomplete (edge safety) ---
    if (!companyRegistered) {
      // Treat as Step One still needed → use CompanyRegistrationGuard in 'incomplete' mode
      setRegGuardMode('incomplete');
      setIsRegGuardOpen(true);
      return;
    }

    // --- CASE: all prereqs satisfied ---
    if (typeof fn === 'function') {
      fn();
    } else {
      onAllowed?.();
    }
  }, [onAllowed]);

  return (
    <>
      {/* Expose guarded click handler via render prop */}
      {typeof children === 'function' ? children({ handleClick }) : null}

      {/* Replaces CompanyStepOneRegistrationGuard */}
      <CompanyRegistrationGuard
        open={isRegGuardOpen}
        mode={regGuardMode} // 'incomplete' | 'agent_pending' (we currently use 'incomplete')
        onClose={() => setIsRegGuardOpen(false)}
        onCompanyInfoClick={() => {
          setIsRegGuardOpen(false);
          navigate('/company/finalregistration');
        }}
        width={regGuardWidth}
      />

      <ScoutPrereqModal
        open={isScoutPrereqOpen}
        onClose={() => setIsScoutPrereqOpen(false)}
        onComplete={() => {
          setIsScoutPrereqOpen(false);
          onAllowed?.(); // optional: mark guard as passed when all prereqs are done
        }}
        companyRegistered={localStorage.getItem('company_is_fully_registered') === '1'}
        jobPublished={localStorage.getItem('initial_job_post_published') === '1'}
      />

      <AgentApprovalPendingModal
        open={isAgentPendingOpen}
        onClose={() => setIsAgentPendingOpen(false)}
        onComplete={() => setIsAgentPendingOpen(false)}
        companyRegistered={localStorage.getItem('company_is_fully_registered') === '1'}
        jobPublished={localStorage.getItem('initial_job_post_published') === '1'}
      />

      <ExpiredRegistrationModal
        open={isExpiredModalOpen}
        onClose={() => setIsExpiredModalOpen(false)}
        onCompanyInfoClick={() => {
          setIsExpiredModalOpen(false);
          navigate('/company/finalregistration');
        }}
        logoSrc={logo}
      />
    </>
  );
};

export default NonSearchSettingsGuard;

